<!--
  REV-21363 — the account's `Dokumente` area (Pure View).

  Selector contract: tests bind to `data-testid` and `data-*` markers, never to styling classes and
  never to translated prose as an anchor. See e2e-lab/account-documents.lab.spec.ts.

  Precedence loading > error > content (pattern: documents-hub, document-detail). Error BEATS
  empty on purpose: a failed load must not render the upload area and thereby claim "this account
  has no documents".

  Where ADR-0009 overrules the live prototype:
    · NO delete control (§4). The prototype still carries a locked trash button whose tooltip reads
      "Löschen gesperrt: Abstimmungsgrundlage für Konto 1021". Button AND lock reason go — without
      a button there is nothing left to justify.
    · the upload input is PDF-only (§12); the prototype allows `application/pdf,image/*`.

  The component knows NOTHING about the surrounding pane chrome (the `Bereich` select and its ×) —
  that belongs to `abcp-account-workspace`, which hosts this area through its `paneContent`.
-->
<div class="ad" data-testid="account-documents">
    <div class="ad-loading" data-testid="account-documents-loading" *ngIf="loading">
        <div class="ad-skeleton ad-skeleton--bar" aria-hidden="true"></div>
        <div class="ad-skeleton ad-skeleton--body" aria-hidden="true"></div>
    </div>

    <div class="ad-error" data-testid="account-documents-error" *ngIf="!loading && error">
        {{ 'abcp_account_documents_error' | i18next }}
    </div>

    <ng-container *ngIf="!loading && !error">
        <!-- LEER — die Upload-Fläche IST der Zustand, keine Liste mit null Zeilen. Und deshalb
             auch KEINE Werkzeugleiste: es gibt nichts zu wählen und nichts zu markieren. -->
        <div
            class="ad-dropzone"
            data-testid="account-documents-dropzone"
            *ngIf="!hasDocuments"
            (click)="onUploadClick()"
        >
            <lucide-icon name="upload" [size]="26" aria-hidden="true"></lucide-icon>
            <div class="ad-dropzone__title">{{ 'abcp_account_documents_dropzone_title' | i18next }}</div>
            <div class="ad-dropzone__or">{{ 'abcp_account_documents_dropzone_or' | i18next }}</div>
            <ham-button
                data-testid="account-documents-dropzone-pick"
                variant="primary"
                size="sm"
                icon="assets/hamIcons/upload.svg"
                (buttonClick)="onUploadClick(); $event.stopPropagation()"
            >
                {{ 'abcp_account_documents_dropzone_pick' | i18next }}
            </ham-button>
        </div>

        <ng-container *ngIf="hasDocuments">
            <!-- WERKZEUGLEISTE — genau ZWEI Aktionen. Kein Papierkorb (§4). -->
            <div class="ad-bar" data-testid="account-documents-toolbar">
                <lucide-icon class="ad-bar__icon" name="file-text" [size]="15" aria-hidden="true"></lucide-icon>

                <!-- Genau 1 Beleg: der Name steht als TEXT. Ein Wähler mit einer Option wäre
                     Bedienung ohne Wahl. -->
                <span
                    class="ad-bar__name"
                    data-testid="account-documents-filename"
                    *ngIf="isSingleDocument"
                    [hamTooltip]="documents[0].filename"
                    tooltipClass="abcp"
                    [tooltipOnlyWhenTruncated]="true"
                    >{{ documents[0].filename }}</span
                >

                <!-- n Belege: `hamSimpleDropDown` — dasselbe Bedienelement, das die Pane-Kopfzeile
                     direkt darüber für ihren `Bereich`-Wähler benutzt, damit die beiden Reihen
                     zusammenpassen.

                     ⚠️ `allowFreeText` MUSS true sein, damit gesucht werden kann: bei `false`
                     abonniert die Direktive das `input`-Ereignis gar nicht erst und blockt jeden
                     Tastendruck per `preventDefault` (dropDown.directive.ts).

                     ⚠️ Und deshalb `(selectionChange)`, NICHT `(ngModelChange)`: bei freiem Text
                     feuert das Modell bei JEDEM Tastendruck. Zusammen mit dem Zurücksetzen unten
                     hätte das die eigene Eingabe nach jedem Zeichen überschrieben — die Suche wäre
                     unbenutzbar. `selectionChange` feuert nur bei einer echten Wahl und liefert
                     bereits die `returnKey`-Id. `optionKeys` ist Anzeige und Suchschlüssel zugleich.

                     ⚠️ Bewusst NICHT `aum-select`: als `mat-form-field appearance="outline"` baute
                     es in dieser 288-px-Leiste 86 px Höhe gegen 28 px des übrigen Reihe, lief auf
                     204 px über seinen Flex-Platz hinaus und legte sich mit `.mat-form-field-flex`
                     ÜBER den Upload-Knopf — der war damit unerreichbar, nicht bloss unschön. -->
                <input
                    hamSimpleDropDown
                    class="ad-bar__select"
                    data-testid="account-documents-select"
                    *ngIf="!isSingleDocument"
                    [options]="options"
                    [optionKeys]="['name']"
                    returnKey="id"
                    [allowFreeText]="true"
                    [ngModel]="documentModel"
                    [ngModelOptions]="{ standalone: true }"
                    (selectionChange)="onSelectionChange($event)"
                />

                <!-- REV-21964 · C1–C4 — WO DIE DATEI STEHT, direkt neben dem Namen.
                     Bewusst LAUT (rot): jedes Dokument in diesem Bereich stuetzt ein Konto, und
                     genau dann ist der Hinweis dringend (C2). Er nimmt nichts weg — der Beleg
                     bleibt, mit seinem Konto und seiner Abstimmung (C1). -->
                <span
                    class="ad-upstream"
                    data-testid="account-documents-upstream-marker"
                    *ngIf="hasUpstreamMarker"
                    [attr.data-state]="selectedDocument?.upstreamState"
                    [hamTooltip]="upstreamStateLabelKey(selectedDocument?.upstreamState) | i18next"
                    tooltipClass="abcp"
                >
                    <lucide-icon
                        [name]="selectedDocument?.upstreamState === 'replaced' ? 'history' : 'unlink'"
                        [size]="13"
                        aria-hidden="true"
                    ></lucide-icon>
                </span>

                <ham-button
                    class="ad-tool"
                    data-testid="account-documents-upload"
                    variant="white"
                    size="toolbar"
                    icon="assets/hamIcons/upload.svg"
                    [hideText]="true"
                    [hamTooltip]="'abcp_account_documents_upload' | i18next"
                    tooltipClass="abcp"
                    (buttonClick)="onUploadClick()"
                ></ham-button>

                <!-- Der Tooltip sagt ausdrücklich, dass das Dokument ERHALTEN bleibt — sonst liest
                     sich "weg aus meiner Liste" wie eine Löschung. Geteilt mit Hub und
                     Einzelseite: gleiche Handlung, gleicher Satz. -->
                <ham-button
                    class="ad-tool"
                    data-testid="account-documents-not-relevant"
                    variant="white"
                    size="toolbar"
                    lucideIcon="eye-off"
                    [hideText]="true"
                    [hamTooltip]="'abcp_documents_not_relevant_mark' | i18next"
                    tooltipClass="abcp"
                    (buttonClick)="onMarkNotRelevant()"
                ></ham-button>
            </div>

            <!-- VORSCHAU — die GETEILTE Komponente (REV-21368s Vorschau, extrahiert). Sie trägt
                 die Schema-Allowlist, die `bypassSecurityTrustResourceUrl` absichert (SRR
                 REV-21368 F1); hier gibt es deshalb keine zweite Kopie davon. -->
            <abcp-document-preview
                *ngIf="!viewerSlot"
                class="ad-preview"
                [previewKind]="selectedDocument?.previewKind ?? null"
                [previewUrl]="selectedDocument?.previewUrl ?? null"
                [title]="selectedDocument?.filename ?? ''"
            ></abcp-document-preview>

            <!-- REV-21369 — der Beleg-Viewer, PROJIZIERT vom Wirt (ADR-0021 E6, am 04.10.2026 auf den
                 Konto-Bereich ausgedehnt). Diese Ansicht holt keine Bytes und zeichnet ihn nicht;
                 sie macht nur Platz. Ohne Projektion bleibt die Vorschau oben. -->
            <div class="ad-viewer" data-testid="account-documents-viewer" *ngIf="viewerSlot">
                <ng-content select="[abcpDocumentViewer]"></ng-content>
            </div>
        </ng-container>
    </ng-container>
</div>
